import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  BarChart3, ArrowRight, DollarSign, TrendingUp, 
  Users, FileText, Clock, PieChart, Sparkles, MessageSquare, Check
} from 'lucide-react';

const ReportingPage = () => {
  const reports = [
    {
      icon: DollarSign,
      title: "Revenue Reports",
      description: "Track total revenue, see trends over time, and understand which services generate the most income."
    },
    {
      icon: TrendingUp,
      title: "Job Completion",
      description: "Monitor completed vs scheduled jobs. Identify bottlenecks and improve operational efficiency."
    },
    {
      icon: Users,
      title: "Crew Productivity",
      description: "See which crews are most productive. Compare jobs completed, time spent, and revenue generated."
    },
    {
      icon: FileText,
      title: "Estimate Conversion",
      description: "Track how many estimates turn into jobs. Understand your sales pipeline and close rate."
    },
    {
      icon: Clock,
      title: "Outstanding Invoices",
      description: "Know exactly how much is owed and how long invoices have been outstanding."
    },
    {
      icon: PieChart,
      title: "Service Mix",
      description: "Understand which services are most popular and profitable for your business."
    }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <BarChart3 className="h-4 w-4" />
                Core Feature
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Reporting & Analytics
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Make data-driven decisions with clear insights into your business performance. 
                Track revenue, productivity, and growth all in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-primary hover:bg-primary/90">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-6">Monthly Overview</h3>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-600 mb-1">Revenue</p>
                  <p className="text-2xl font-bold text-emerald-700">$48,250</p>
                  <p className="text-xs text-emerald-600">+12% vs last month</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Jobs Completed</p>
                  <p className="text-2xl font-bold text-blue-700">387</p>
                  <p className="text-xs text-blue-600">98% completion rate</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Outstanding Invoices</span>
                  <span className="font-semibold text-amber-600">$4,200</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Estimate Conversion</span>
                  <span className="font-semibold text-gray-900">78%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avg Job Value</span>
                  <span className="font-semibold text-gray-900">$124.68</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reports Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Reports</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to understand how your business is performing and where you can improve.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reports.map((report, index) => (
              <Card key={index} className="border-gray-100 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <report.icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{report.title}</h3>
                  <p className="text-gray-600">{report.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Data-Driven Decisions
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Stop guessing and start knowing. With clear visibility into your business metrics, 
                you can make confident decisions about pricing, hiring, and growth.
              </p>
              <ul className="space-y-4">
                {[
                  "Know which services are most profitable",
                  "Identify your top-performing crews",
                  "Spot trends before they become problems",
                  "Set goals and track progress",
                  "Share reports with stakeholders"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ArrowRight className="h-3 w-3 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <TrendingUp className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">+23%</p>
                <p className="text-sm text-gray-500">Revenue Growth</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <Users className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">98%</p>
                <p className="text-sm text-gray-500">Job Completion</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <FileText className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">78%</p>
                <p className="text-sm text-gray-500">Estimate Conversion</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <Clock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">2.3 days</p>
                <p className="text-sm text-gray-500">Avg Payment Time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Get Insights by Simply Asking
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                No more digging through reports. Our AI Assistant gives you instant answers to 
                business questions—just ask in plain English.
              </p>
              <ul className="space-y-4">
                {[
                  "Ask about revenue trends instantly",
                  "Get job completion stats on demand",
                  "Understand crew performance quickly",
                  "Forecast based on historical data"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features/ai-assistant">
                  <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                    Learn About AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-violet-100 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-white">AI Assistant</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-violet-50 rounded-lg p-4 ml-8">
                  <p className="text-sm text-gray-700">"How did we do this month compared to last month?"</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mr-8">
                  <p className="text-sm text-gray-700">Here's your month-over-month comparison:</p>
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Revenue</span>
                      <span className="font-medium text-emerald-600">↑ 12% ($48,250)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Jobs Completed</span>
                      <span className="font-medium text-emerald-600">↑ 8% (387)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>New Customers</span>
                      <span className="font-medium text-emerald-600">↑ 3 (15 total)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Avg Job Value</span>
                      <span className="font-medium text-blue-600">→ $124.68</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            See Your Business Clearly
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Get the insights you need to grow your landscaping business with confidence.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default ReportingPage;
